import { useApolloClient } from '@apollo/client';
import { UseFormReturn } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { invalidateCacheField } from '@/graphql/client.utils';
import {
  EntityImportTableForm,
  NAMESPACE,
} from '@/modules/import/EntityImportTable.types';
import { mapFormDataToMutation } from '@/modules/import/EntityImportTable.utils';
import { useEntityTableImportMutation } from '@/modules/import/graphql/EntityTableImport.generated';
import { useDefaultAssetClassId } from '@/modules/tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';

import { PaneForm } from '../../../ManageIntegrationDataModal.types';
import {
  hasUntypedSelectedEntities,
  withImportSelectionsCleared,
  withoutClaimedEntities,
} from '../importData.utils';

export interface EntitiesImportForm extends PaneForm {
  formMethods: UseFormReturn<EntityImportTableForm>;
}

/**
 * The "Import data" → Entities form. `EntityImportTable` fills the form itself
 * as its query resolves, so unlike the other panes there is nothing to seed.
 *
 * @param getClaimedIntegrationEntityIds integration entities the link panes
 * have spoken for, which this pane must not also import as new.
 */
export function useEntitiesImportForm(
  getClaimedIntegrationEntityIds: () => Set<string>,
  onImported?: () => void
): EntitiesImportForm {
  const apolloClient = useApolloClient();
  const { createErrorFeedback, showFeedback } = useFeedback();
  const { assetClassesById } = useTenantDetailsContext();
  const defaultAssetClassId = useDefaultAssetClassId();

  const formMethods = useForm<EntityImportTableForm>({
    defaultValues: { [NAMESPACE]: { householdMap: {} } },
  });
  const { handleSubmit, reset } = formMethods;

  // No `onError`: Apollo only rejects the promise when the caller hasn't
  // handled the error, and the commit needs that rejection to stop.
  const [importEntities] = useEntityTableImportMutation();

  const save = async () => {
    let canContinue = true;
    const submit = handleSubmit(
      async (values) => {
        // Rows are listed as importable because they had no Luminary entity
        // attached when this pane's query ran. The "Link data" step may have
        // attached one since, so drop anything the link panes have claimed —
        // otherwise Done would link the entity *and* duplicate it.
        const importable = withoutClaimedEntities(
          values,
          getClaimedIntegrationEntityIds()
        );

        if (hasUntypedSelectedEntities(importable)) {
          const message =
            'Please select an entity type for all selected entities.';
          showFeedback(message, { variant: 'info-high' });
          canContinue = false;
          return;
        }

        const inputs = mapFormDataToMutation(importable, {
          defaultAssetClassId,
          assetClassesById,
        });
        if (!inputs.length) return;

        try {
          await importEntities({ variables: { inputs } });
        } catch (error) {
          createErrorFeedback(
            'There was an error importing entities. Please try again.'
          );
          throw error;
        }

        onImported?.();
        invalidateCacheField('entities', apolloClient);

        // Untick what just landed, and make that the baseline: if a later pane
        // fails and the user presses Done again, these must not be sent twice.
        reset(withImportSelectionsCleared(values));
      },
      () => {
        // Return normally so React Hook Form can set `isSubmitted`. The form
        // inputs use that state to display their errors and red borders.
        canContinue = false;
      }
    );

    await submit();
    return canContinue;
  };

  return { formMethods, save };
}
